import React, {FC, useState} from "react";
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import {IGetAllLocalizationLanguagesBodyRes} from "@evennt-tech/api-v2-external";
import CountryFlag from "view/components/CountryFlag";
import FlexBox from "view/components/Layout/FlexBox";
import LanguageName from "view/components/LanguageName";
import {ArrowRight} from "@material-ui/icons";
import {grey} from "@material-ui/core/colors";
import useStyles from "./styles";

type LanguagePickerProps = {
  languages: IGetAllLocalizationLanguagesBodyRes[];
  currentCode?: string;
  onSelect: (code: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  isDark?: boolean;
};

const LanguagePicker: FC<LanguagePickerProps> = ({
  languages,
  currentCode,
  onSelect,
  disabled,
  id = "language-picker",
  className,
  isDark,
}) => {
  const classes = useStyles({isDark});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!languages.length) {
    return null;
  }

  const current =
    languages.find((language) => language.code === currentCode) ?? languages[0];

  const handleSelect = (code: string) => {
    setAnchorEl(null);

    if (code !== currentCode) {
      onSelect(code);
    }
  };

  return (
    <>
      <Button
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        disabled={disabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className={clsx(classes.trigger, className)}
      >
        <FlexBox alignCenter gap={1}>
          <CountryFlag size="small" flagEmoji={current.flagEmoji} />
          <Typography variant="h4" className={classes.triggerLabel}>
            <LanguageName language={current} />
          </Typography>
          <ArrowRight
            fontSize="small"
            htmlColor={isDark ? "#FFFFFF" : grey[400]}
          />
        </FlexBox>
      </Button>

      <Menu
        id={id}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={() => setAnchorEl(null)}
        classes={{paper: classes.paper}}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleSelect(language.code)}
            className={classes.item}
            disableGutters
          >
            <FlexBox alignCenter gap={1} fullWidth>
              <CountryFlag size="medium" flagEmoji={language.flagEmoji} />
              <Typography
                variant="h4"
                className={clsx(
                  classes.itemLabel,
                  language.code === currentCode
                    ? classes.itemLabelSelected
                    : classes.itemLabelUnselected
                )}
              >
                <LanguageName language={language} />
              </Typography>
            </FlexBox>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguagePicker;
